import axios, { InternalAxiosRequestConfig } from 'axios';
import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import type { RequestOptions, extOptionsType } from '@/types/index';
import axiosRetry from 'axios-retry';
import { getQueryString } from '@/utils/url';
import { cgiModeMap, codeMessage } from '@/service/const';
import { v4 as uuidv4 } from 'uuid';
import { NotifyPlugin } from 'tdesign-vue-next';
import { customReport, reportCacheStatus, showTips } from '@/report/custom_report';
import { isFunction, isObject, isPlainObject, isUndefined, mapValues } from 'lodash-es';
import { useEnv } from '@/utils/env';
import { unRefAndCloneDeep } from '@/utils/reactive';
import { showUniqueDialog, MsgType } from '@/utils/unique_dialog';
import pako from 'pako';
import { redirectToTaihuLogin } from '@/utils/taihu-auth';

function transformParamsValue(v: unknown, config: InternalAxiosRequestConfig<any>): unknown {
  const isGet = config.method === 'get';
  const value = unRefAndCloneDeep(v);
  switch (true) {
    case isFunction(value): {
      return transformParamsValue((value as Function)(), config);
    }
    case isGet && isObject(value): {
      // 对于Get请求如果传递的对象参数则序列化为字符串
      let str: string;
      try {
        str = JSON.stringify(value);
      } catch (e) {
        str = value as unknown as string;
      }
      return str;
    }
    default:
      return value;
  }
}

function checkIsNendGzip(params: Record<string, any>, config: AxiosRequestConfig) {
  const { is_gzip_body: isGzipBody } = params || {};
  const { method = 'get' } = config;
  return `${isGzipBody}` === '1' && ['post', 'put'].includes(method?.toLocaleLowerCase());
}

export function transformParams(config: any) {
  return isPlainObject(config.params)
    ? mapValues(config.params, (value) => transformParamsValue(value, config))
    : config.params;
}

export interface TRequestOptions extends RequestOptions {
  getGame?: Function;
  disableRequestFromActiveCache?: boolean;
  defaultValue?: any;
}

export class HttpRequest {
  defaults: {
    baseUrl: string;
  };
  options: AxiosRequestConfig;
  interceptorRequest?: Function;
  interceptorResponse?: Function;
  extOptions: extOptionsType;
  trace2Refer: Record<string, string>;
  constructor(options: RequestOptions, extOptions: extOptionsType = {}) {
    this.defaults = {
      baseUrl: '',
    };
    this.interceptorRequest = options.interceptorRequest;
    this.interceptorResponse = options.interceptorResponse;
    this.options = Object.assign(this.defaults, options);
    this.extOptions = extOptions;
    this.trace2Refer = {};
  }

  interceptors(instance: AxiosInstance) {
    const { isUseReuqest = true, isUseResponse = true } = this.extOptions;
    isUseReuqest &&
      instance.interceptors.request.use(
        (config: TRequestOptions) => {
          // TODO 根据 dbMode 来链接不同的DB环境，透传给api，让api来继续透传
          /**
           * 指定后台环境
           * cgiMode有以下几种参数  dev  prerelease  production
           */
          const { env } = useEnv();
          const cgiMode: string = getQueryString('cgiMode') || env.value;
          /**
           * 指定db环境
           * dbMode 有以下几种参数  dev  prerelease  production
           */
          const dbMode = getQueryString('dbMode') || env.value;
          const customGame = isFunction(config.getGame) ? config.getGame() : undefined;
          const game = customGame || getQueryString('game');
          const noCache = getQueryString('no_cache');
          const updateCache = getQueryString('update_cache');
          const requestDomain = cgiModeMap[cgiMode as keyof typeof cgiModeMap];
          const baseURL = requestDomain ? `https://${requestDomain}` : '';
          const traceId = uuidv4();
          const commonHeaders: any = {
            'x-trace-id': traceId,
            game,
          };
          this.trace2Refer[traceId] = location.href;

          if (window?.aegis?.bean) commonHeaders['session-id'] = window.aegis.bean.sessionId;

          const source = axios.CancelToken.source();
          // eslint-disable-next-line no-param-reassign
          config.cancelToken = source.token;
          window?.reqList?.push?.({ url: config.url as string, id: traceId, source });

          const params = transformParams(config);

          const baseQueryParams = {
            ...params,
            dbMode,
            cgiMode,
            __game: game,
            ...(customGame ? { game: customGame } : {}),
          };
          if (!isUndefined(noCache) && `${noCache}`.length > 0) {
            baseQueryParams.__no_cache = noCache;
          }
          if (!isUndefined(updateCache) && `${updateCache}`.length > 0) {
            baseQueryParams.__update_cache = updateCache;
          }
          const newConfig = {
            ...config,
            params: baseQueryParams,
            baseURL: baseURL || config.baseURL,
            headers: {
              ...config.headers,
              ...commonHeaders,
            },
          };

          // 判断是否需要gzip压缩请求请求体
          const needGzipBody = checkIsNendGzip(params, newConfig);
          if (needGzipBody) {
            const uint8Array = pako.gzip(JSON.stringify(newConfig.data));
            const uint8Str = uint8Array.reduce((prev, next) => prev + String.fromCharCode(next), '');
            const gzipStr = window.btoa(uint8Str);
            newConfig.headers = {
              ...newConfig.headers,
              'Content-Type': 'text/plain',
            };
            newConfig.data = gzipStr;
          }
          return this.interceptorRequest?.(newConfig) || newConfig;
        },
        (err) => Promise.reject(err),
      );
    isUseResponse &&
      instance.interceptors.response.use(
        (res) => {
          // TODO 增加接口数据上报
          // TODO 增加返回码
          /**
           * 错误码校验
           * 先判断http状态码是否正常
           * 在判断body中的ret是否为0 0为成功 其他为失败
           */
          const { aegisMethod: method = '', aegisUrl: url = '' } = res?.request ?? {};
          const {
            data: requestData = {},
            headers = { 'x-trace-id': '', game: '' },
            url: resCfgUrl = '',
          } = res?.config ?? {};
          const { 'x-trace-id': id } = headers;
          const { date = '', xHitCache = '' } = res?.headers ?? {};
          const { status }: { status: number } = res ?? {};
          const refer = this.trace2Refer[id] || '';

          // 请求完成，清除列表里的数据
          window.reqList = window?.reqList?.filter?.((item) => item.id === id) ?? [];

          // 将requestData转化成对象,并由reqDataRecord接收
          let reqDataRecord = {};
          if (isObject(requestData)) {
            reqDataRecord = requestData;
          } else {
            try {
              reqDataRecord = JSON.parse(requestData);
            } catch (_e: any) {
              reqDataRecord = { data: requestData };
            }
          }

          const reqData = {
            method,
            data: {
              ...reqDataRecord,
              __game: headers?.game,
            },
            headers: {
              ...headers,
            },
            url,
          };

          // 数据上报
          reportCacheStatus(url, Date.now() - new Date(date as string).getTime(), xHitCache);

          // HTTP status code check
          if (status in codeMessage) {
            if (this.interceptorResponse) {
              return this.interceptorResponse?.(res);
            }
            const errorText = codeMessage[status as keyof typeof codeMessage];
            NotifyPlugin.error({
              title: resCfgUrl,
              content: errorText,
            });
            if (+status === 401) {
              redirectToTaihuLogin();
            }
            throw errorText;
          }

          // res?.data 是aixos的响应体，接口真正的回包
          const responseBody = res?.data;
          if (!isObject(responseBody)) {
            const isExpectDataIsObject = (res?.config as any)?.notCheckData !== true && (res?.config as any)?.responseType !== 'stream';
            if (isExpectDataIsObject) {
              const errRet = -10000;
              const errMsg = 'resData is not a object';
              // 接口请求的路径
              const requestUrl = res?.config?.url || url;
              customReport(
                {
                  msg: `${errMsg} ! (url: ${requestUrl}, data: ${responseBody})`,
                  resData: responseBody,
                },
                requestUrl,
                reqData,
                errRet,
                { refer },
              );
              showTips(errRet, errMsg, responseBody);
              return false;
            }
            return responseBody;
          }

          const {
            data: { data, message, msg, err },
          } = res as any;
          const ret = Number((responseBody as any)?.ret);

          if (ret !== 0) {
            customReport(message || msg, url, reqData, ret, { refer });
            if (ret === 30000) {
              redirectToTaihuLogin();
              return;
            }
            if (this.interceptorResponse) {
              return this.interceptorResponse?.(res);
            }
            showTips(ret, message || msg, err || url);
          }
          return data;
        },
        (err) => {
          // Show Error
          const { url, params = {}, method } = err?.response?.config || {};
          const status = err?.response?.status;
          const { headers = { 'x-trace-id': '' } } = err?.config ?? {};
          const { 'x-trace-id': id } = headers;
          const refer = this.trace2Refer[id] || '';
          if (err?.message === 'cancel req') {
            return Promise.reject(err);
          }

          const isNonErrInfo = !status && !method && !url;
          let errInfo;
          if (isNonErrInfo) {
            let content;
            try {
              content = JSON.stringify(err);
            } catch (e) {
              content = 'err json stringify fail';
            }
            errInfo = {
              title: 'Network Unknow Error',
              content,
              msgType: MsgType.ERROR,
              isMerge: true,
            };
          } else {
            errInfo = {
              title: `Network Error: ${status}`,
              content: `${method} url: ${url}`,
              msgType: MsgType.ERROR,
              isMerge: true,
            };
          }
          console.log(errInfo);
          // showUniqueDialog(errInfo);
          if (+status === 401) {
            redirectToTaihuLogin();
          }

          const reportData = {
            _cuz_report_: 1,
            url,
            params: {
              params,
              method,
              status,
            },
            error_message: err?.message || '',
          };

          // 添加上报
          const { needReport = true } = err ?? {};
          if (needReport) {
            customReport(reportData, '', {}, 0, { refer });
          }

          return Promise.reject(err);
        },
      );
  }

  createInstance() {
    const instance = axios.create(this.options);
    // 是否使用重试功能
    const { isUseRetry = true } = this.extOptions;

    // 增加重试逻辑
    isUseRetry && axiosRetry(instance, { retries: 3 });
    this.interceptors(instance);
    return instance;
  }
}
